import { useState, useEffect, useMemo, Fragment } from 'react';

import useUnsavedLeaveStore, { UNSAVED_SCOPE } from '@shared/model/useUnsavedLeaveStore';
import AdminLayout from '@shared/components/organisms/AdminLayout/AdminLayout';
import TabContentLayout from '@shared/components/organisms/TabContentLayout/TabContentLayout';
import ContentCard from '@shared/components/organisms/ContentCard/ContentCard';
import Button from '@shared/components/atoms/Button/Button';
import Alert from '@shared/components/atoms/Alert';
import Confirm from '@shared/components/atoms/Confirm/Confirm';
import { useApprovalGuideQuery } from '@features/approval-guide/hooks/useApprovalGuideQuery';
import { useApprovalGuideMutation } from '@features/approval-guide/hooks/useApprovalGuideMutation';
import { useAlert } from '@shared/hooks';
import dragIcon from '@shared/assets/images/drag_icon.svg';

import styles from './ApprovalLineReferenceManagementPage.module.scss';

/**
 * ApprovalLineReferenceManagementPage
 * - 관리자 결재라인 안내 관리 페이지
 * - 사이드바의 결재라인 참고 모달 내용을 페이지에 직접 표시
 */
export default function ApprovalLineReferenceManagementPage() {
  const { data, isLoading, isError, error } = useApprovalGuideQuery({
    enabled: true,
  });

  const [guides, setGuides] = useState([]);
  const [originalGuides, setOriginalGuides] = useState([]);

  // 드래그 앤 드롭 상태
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null);

  const { alertState, showAlert, closeAlert } = useAlert();

  // LNB/로그아웃 시 저장 없이 이동 확인용 store (mutation onSuccess에서 사용하므로 상단에 정의)
  const pendingLeave = useUnsavedLeaveStore((state) => state.pendingLeave);
  const confirmLeave = useUnsavedLeaveStore((state) => state.confirmLeave);
  const cancelLeave = useUnsavedLeaveStore((state) => state.cancelLeave);
  const setUnsaved = useUnsavedLeaveStore((state) => state.setUnsaved);
  const clearUnsaved = useUnsavedLeaveStore((state) => state.clearUnsaved);

  const { mutate: updateGuide, isPending: isSaving } = useApprovalGuideMutation({
    onSuccess: function () {
      // 성공 시 원본 데이터도 업데이트
      setOriginalGuides(guides);
      clearUnsaved();
      showAlert('저장되었습니다.');
    },
    onError: function (error) {
      showAlert(error?.message || '저장 중 오류가 발생했습니다.');
    },
  });

  // 데이터가 로드되면 guides 상태 업데이트
  useEffect(
    function () {
      if (data?.guides) {
        const guidesData = data.guides.map((guide) => ({
          ...guide,
          firstStepRequired: guide.firstStepRequired || false,
          secondStepRequired: guide.secondStepRequired || false,
          thirdStepRequired: guide.thirdStepRequired || false,
          agreementRequired: guide.agreementRequired || false,
          ceoRequired: guide.ceoRequired || false,
          sortOrder: guide.sortOrder || 0,
        }));
        setGuides(guidesData);
        setOriginalGuides(guidesData);
      }
    },
    [data],
  );

  // 체크박스 변경 핸들러
  function handleCheckboxChange(guideIdx, field) {
    setGuides((prevGuides) =>
      prevGuides.map((guide) => {
        if (guide.idx === guideIdx) {
          return {
            ...guide,
            [field]: !guide[field],
          };
        }
        return guide;
      }),
    );
  }

  // 변경된 항목만 필터링
  const changedGuides = useMemo(
    function () {
      return guides.filter(function (guide) {
        const original = originalGuides.find((og) => og.idx === guide.idx);
        if (!original) return false;

        // sortOrder는 숫자로 비교 (타입 불일치 방지)
        const currentSortOrder = Number(guide.sortOrder) || 0;
        const originalSortOrder = Number(original.sortOrder) || 0;

        return (
          guide.firstStepRequired !== original.firstStepRequired ||
          guide.secondStepRequired !== original.secondStepRequired ||
          guide.thirdStepRequired !== original.thirdStepRequired ||
          guide.agreementRequired !== original.agreementRequired ||
          guide.ceoRequired !== original.ceoRequired ||
          currentSortOrder !== originalSortOrder
        );
      });
    },
    [guides, originalGuides],
  );

  // 저장 없이 이동 시 확인: 변경사항 있을 때만 store 등록
  useEffect(
    function () {
      if (changedGuides.length > 0) {
        setUnsaved(UNSAVED_SCOPE.APPROVAL_LINE_GUIDE, function getResetAndProceed() {
          return function runResetThenNext(next) {
            setGuides([...originalGuides]);
            clearUnsaved();
            if (typeof next === 'function') next();
          };
        });
      } else {
        clearUnsaved();
      }
      return function cleanup() {
        clearUnsaved();
      };
    },
    [changedGuides.length, originalGuides, setUnsaved, clearUnsaved],
  );

  // 드래그 시작
  function handleDragStart(e, index) {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  }

  // 드래그 종료
  function handleDragEnd(e) {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 드래그 오버 (드롭 가능 영역 표시)
  function handleDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  // 카드 위에서 드래그 오버 (위/아래 구분)
  function handleCardDragOver(e, index) {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null) {
      return;
    }

    if (draggedIndex === index) {
      setDragOverIndex(null);
      setDragOverPosition(null);
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY;
    const cardMiddle = rect.top + rect.height / 2;

    const position = mouseY < cardMiddle ? 'top' : 'bottom';

    setDragOverIndex(index);
    setDragOverPosition(position);
  }

  // 카드에서 드래그 리브
  function handleCardDragLeave(e) {
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 드롭 (순서 변경 및 sortOrder 값 재계산)
  function handleDrop(e, dropIndex) {
    e.preventDefault();
    if (draggedIndex === null) {
      return;
    }

    if (draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setDragOverPosition(null);
      return;
    }

    const updated = [...guides];
    const draggedItem = updated[draggedIndex];

    // dragOverPosition에 따라 삽입 위치 결정 (제거 전에 계산)
    let insertIndex = dropIndex;
    if (dragOverPosition === 'bottom') {
      insertIndex = dropIndex + 1;
    }

    // 드래그된 아이템 제거
    updated.splice(draggedIndex, 1);

    // 드래그된 아이템이 제거되면서 insertIndex 조정
    // draggedIndex보다 큰 인덱스는 모두 1씩 감소
    if (draggedIndex < insertIndex) {
      insertIndex = insertIndex - 1;
    }

    // 드롭 위치에 삽입
    updated.splice(insertIndex, 0, draggedItem);

    // sortOrder 값 재계산 (새 객체 생성하여 React가 변경을 감지하도록)
    const updatedWithSortOrder = updated.map(function (item, idx) {
      return {
        ...item,
        sortOrder: idx + 1,
      };
    });

    setGuides(updatedWithSortOrder);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverPosition(null);
  }

  // 저장 버튼 클릭 핸들러
  function handleSave() {
    if (changedGuides.length === 0) {
      showAlert('변경된 내용이 없습니다.');
      return;
    }

    // sortOrder가 변경된 항목이 있는지 확인
    const hasSortOrderChange = changedGuides.some(function (guide) {
      const original = originalGuides.find((og) => og.idx === guide.idx);
      if (!original) return false;
      const currentSortOrder = Number(guide.sortOrder) || 0;
      const originalSortOrder = Number(original.sortOrder) || 0;
      return currentSortOrder !== originalSortOrder;
    });

    // 순서가 변경된 경우 모든 항목의 sortOrder를 업데이트해야 함
    // 순서가 변경되지 않은 경우에는 변경된 항목만 전송
    const guidesToUpdate = hasSortOrderChange ? guides : changedGuides;

    const updateData = guidesToUpdate.map((guide) => ({
      idx: guide.idx,
      firstStepRequired: guide.firstStepRequired || false,
      secondStepRequired: guide.secondStepRequired || false,
      thirdStepRequired: guide.thirdStepRequired || false,
      agreementRequired: guide.agreementRequired || false,
      ceoRequired: guide.ceoRequired || false,
      sortOrder: guide.sortOrder || 0,
      // 참고: guideTitle, reviewerDept, recipient는 백엔드 DTO에 없고
      // 백엔드 SQL도 이 필드들을 업데이트하지 않으므로 보내지 않음
      // 백엔드가 이 필드들을 업데이트하지 않으므로 기존 값이 유지됨
    }));

    updateGuide(updateData);
  }

  return (
    <AdminLayout>
      <TabContentLayout
        tabs={{
          label: '결재라인 안내 관리',
          content: (
            <ContentCard className={styles.contentCardMarginFix}>
              <div className={styles.contentWrapper}>
                <div className={styles.contentContainer}>
                  <h2 className={styles.title}>결재라인 안내</h2>

                  {isLoading && (
                    <div className={styles.loadingContainer}>
                      <div className={styles.circularProgress} role="status" />
                      <div className={styles.loadingMessage}>가이드라인 로딩 중...</div>
                    </div>
                  )}

                  {isError && (
                    <div className={styles.errorMessage}>
                      {error instanceof Error
                        ? error.message
                        : '가이드라인을 불러오는 중 오류가 발생했습니다.'}
                    </div>
                  )}

                  {!isLoading && !isError && data && (
                    <>
                      <h3 className={styles.subTitle}>
                        <span>양식별 결재라인</span>
                      </h3>
                      <div className={styles.formGuideTableContainer}>
                        <table className={styles.formGuideTable}>
                          <thead>
                            <tr>
                              <th rowSpan="2"></th>
                              <th rowSpan="2">해당 양식</th>
                              <th colSpan="6">결재라인</th>
                              <th rowSpan="2">참조</th>
                            </tr>
                            <tr>
                              <th>검토</th>
                              <th>1차</th>
                              <th>2차</th>
                              <th>3차</th>
                              <th>
                                합의
                                <br />
                                (경영기획본부장)
                              </th>
                              <th>대표이사</th>
                            </tr>
                          </thead>
                          <tbody onDragOver={handleDragOver}>
                            {guides.length > 0 ? (
                              guides.map((guide, index) => {
                                const isDragged = draggedIndex === index;
                                const isDragOver = dragOverIndex === index;
                                const showTopLine = isDragOver && dragOverPosition === 'top';
                                const showBottomLine = isDragOver && dragOverPosition === 'bottom';

                                return (
                                  <Fragment key={guide.idx}>
                                    {/* 위쪽 드롭 인디케이터 */}
                                    {showTopLine && (
                                      <tr>
                                        <td colSpan="9" className={styles.dropIndicator}>
                                          <div className={styles.dropLine} />
                                        </td>
                                      </tr>
                                    )}
                                    {/* 데이터 행 */}
                                    <tr
                                      className={isDragged ? styles.dragging : ''}
                                      draggable
                                      onDragStart={function (e) {
                                        handleDragStart(e, index);
                                      }}
                                      onDragOver={function (e) {
                                        handleCardDragOver(e, index);
                                      }}
                                      onDragLeave={handleCardDragLeave}
                                      onDragEnd={handleDragEnd}
                                      onDrop={function (e) {
                                        handleDrop(e, index);
                                      }}
                                    >
                                      <td className={styles.dragCell}>
                                        <img
                                          src={dragIcon}
                                          alt="드래그"
                                          className={styles.dragIcon}
                                        />
                                      </td>
                                      <td>{guide.guideTitle}</td>
                                      <td>{guide.reviewerDept || ''}</td>
                                      <td className={styles.centeredCell}>
                                        <input
                                          type="checkbox"
                                          checked={guide.firstStepRequired || false}
                                          onChange={() =>
                                            handleCheckboxChange(guide.idx, 'firstStepRequired')
                                          }
                                          className={styles.checkbox}
                                        />
                                      </td>
                                      <td className={styles.centeredCell}>
                                        <input
                                          type="checkbox"
                                          checked={guide.secondStepRequired || false}
                                          onChange={() =>
                                            handleCheckboxChange(guide.idx, 'secondStepRequired')
                                          }
                                          className={styles.checkbox}
                                        />
                                      </td>
                                      <td className={styles.centeredCell}>
                                        <input
                                          type="checkbox"
                                          checked={guide.thirdStepRequired || false}
                                          onChange={() =>
                                            handleCheckboxChange(guide.idx, 'thirdStepRequired')
                                          }
                                          className={styles.checkbox}
                                        />
                                      </td>
                                      <td className={styles.centeredCell}>
                                        <input
                                          type="checkbox"
                                          checked={guide.agreementRequired || false}
                                          onChange={() =>
                                            handleCheckboxChange(guide.idx, 'agreementRequired')
                                          }
                                          className={styles.checkbox}
                                        />
                                      </td>
                                      <td className={styles.centeredCell}>
                                        <input
                                          type="checkbox"
                                          checked={guide.ceoRequired || false}
                                          onChange={() =>
                                            handleCheckboxChange(guide.idx, 'ceoRequired')
                                          }
                                          className={styles.checkbox}
                                        />
                                      </td>
                                      <td>{guide.recipient || ''}</td>
                                    </tr>
                                    {/* 아래쪽 드롭 인디케이터 */}
                                    {showBottomLine && (
                                      <tr>
                                        <td colSpan="9" className={styles.dropIndicator}>
                                          <div className={styles.dropLine} />
                                        </td>
                                      </tr>
                                    )}
                                  </Fragment>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan="9" className={styles.centeredCell}>
                                  일치하는 양식이 없습니다.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      {/* 저장 버튼 */}
                      <div className={styles.saveButtonWrapper}>
                        <Button
                          type="button"
                          variant="primary"
                          onClick={handleSave}
                          disabled={isSaving || changedGuides.length === 0}
                        >
                          저장
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </ContentCard>
          ),
        }}
      />
      <Alert isOpen={alertState.isOpen} message={alertState.message} onClose={closeAlert} />
      {/* LNB/로그아웃 클릭 시 저장 없이 이동 확인 (store의 pendingLeave) */}
      <Confirm
        isOpen={Boolean(pendingLeave)}
        message="작성 중인 내용이 있습니다. 저장하지 않고 이동하시겠습니까?"
        onConfirm={confirmLeave}
        onCancel={cancelLeave}
      />
    </AdminLayout>
  );
}
